import React from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Cell,
} from "recharts";
import { Star, ThumbsUp, ShieldCheck, MessageSquareHeart } from "lucide-react";

const SAFFRON = "#E67E22";
const SAFFRON_LIGHT = "#F8BE7B";
const STONE = "#78716C";

export default function AdminStats({ stats }) {
  if (!stats) return null;
  const cards = [
    { label: "Total Responses", value: stats.total, icon: MessageSquareHeart, sub: "Submissions to date" },
    { label: "Average Rating", value: `${stats.avg_rating || 0}★`, icon: Star, sub: "Out of 5 stars (Q40)" },
    { label: "Would Recommend", value: `${stats.recommend_pct || 0}%`, icon: ThumbsUp, sub: "Said Yes / Definitely (Q29)" },
    { label: "Trust & Confidence", value: `${stats.trust_pct || 0}%`, icon: ShieldCheck, sub: "Excellent + Very Good (Q30)" },
  ];

  return (
    <div data-testid="admin-stats" className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div
            key={c.label}
            data-testid={`stat-card-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
            className="bg-white border border-stone-200 rounded-2xl p-5"
          >
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-medium">{c.label}</div>
              <div className="w-9 h-9 rounded-lg bg-saffron-100 text-saffron-600 flex items-center justify-center">
                <c.icon className="w-4 h-4" strokeWidth={1.75} />
              </div>
            </div>
            <div className="mt-3 font-serif text-3xl text-stone-900">{c.value}</div>
            <div className="text-xs text-stone-500 mt-1">{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="chart-satisfaction">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-medium">Overall Satisfaction (Q28)</div>
          <div className="mt-2 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.satisfaction_breakdown} margin={{ top: 16, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F5F5F4" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: STONE }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: STONE }} />
                <Tooltip cursor={{ fill: "#FDEBD0" }} contentStyle={{ border: "1px solid #E7E5E4", borderRadius: 8 }} />
                <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                  {stats.satisfaction_breakdown?.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? SAFFRON : i === 1 ? SAFFRON_LIGHT : "#D6D3D1"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="chart-services">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-medium">Most Helpful Services (Q32)</div>
          <div className="mt-2 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.service_counts} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F5F5F4" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: STONE }} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 11, fill: STONE }} width={140} />
                <Tooltip cursor={{ fill: "#FDEBD0" }} contentStyle={{ border: "1px solid #E7E5E4", borderRadius: 8 }} />
                <Bar dataKey="count" fill={SAFFRON} radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {stats.monthly_trend?.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="chart-trend">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-medium">Submissions Over Time</div>
          <div className="mt-2 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.monthly_trend} margin={{ top: 16, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F5F5F4" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: STONE }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: STONE }} />
                <Tooltip contentStyle={{ border: "1px solid #E7E5E4", borderRadius: 8 }} />
                <Line type="monotone" dataKey="count" stroke={SAFFRON} strokeWidth={2.5} dot={{ r: 4, fill: SAFFRON }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
